// AI-Call editable ranked recall list — pure helpers (Phase D.2b).
//
// The backend (POST /campaigns/ai-call/preview-ranking) returns the audience in dial
// order with per-row signals. The practice can PIN patients (float to top) and EXCLUDE
// patients (struck through, not dialed). We apply pin/exclude CLIENT-SIDE for snappy UI;
// the backend re-applies them authoritatively at launch from form.priorityConfig.
//
// For the 'standard' preset the backend returns rows in original order with the signal
// fields (monthsOverdue / txValue / insuranceRemaining / why) UNDEFINED — every helper
// here must render those gracefully (no "$undefined" / "undefined mo").
//
// KEEP IN SYNC WITH:
//   - src/pages/Campaigns/components/CreateCampaignModal.vue (ranked table + payload)
//   - cypress/node-tests/ai-call-ranking.test.js

// Stale-fetch guard for overlapping /preview-ranking requests. Each fetch grabs a
// monotonic `seq`; if a newer fetch has since bumped the component's counter, the older
// one is stale and must NOT apply its result / clear loading. Returns true when stale.
export function isStaleRankingFetch(seq, currentSeq) {
  return seq !== currentSeq;
}

// 'standard'/absent preset is inert: no ranked list, no endpoint call.
export function shouldFetchRanking(formType, isAiCallEnabled, preset, selectedListLength) {
  return (
    formType === 'ai_call' &&
    !!isAiCallEnabled &&
    !!preset &&
    preset !== 'standard' &&
    Number(selectedListLength) > 0
  );
}

// Format a USD signal (txValue / insuranceRemaining). Returns '' for missing/invalid.
export function formatMoneySignal(value) {
  if (value == null || value === '' || isNaN(Number(value))) return '';
  return `$${Math.round(Number(value)).toLocaleString()}`;
}

// Format months-overdue. Returns '' for missing/invalid.
export function formatOverdueSignal(months) {
  if (months == null || months === '' || isNaN(Number(months))) return '';
  const m = Math.round(Number(months));
  return `${m} mo overdue`;
}

/**
 * Build the human-readable "why" cell from the row's signals. Prefers the backend-
 * supplied `why` string; otherwise assembles from the individual signals, omitting any
 * missing part. Returns '' when nothing is available (standard preset rows).
 */
export function buildWhy(row) {
  if (!row) return '';
  if (typeof row.why === 'string' && row.why.trim()) return row.why.trim();
  const parts = [
    formatOverdueSignal(row.monthsOverdue),
    formatMoneySignal(row.txValue) ? `${formatMoneySignal(row.txValue)} tx` : '',
    formatMoneySignal(row.insuranceRemaining) ? `${formatMoneySignal(row.insuranceRemaining)} insurance` : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

// Display name: "FName LName", falling back to the PatNum when both names are missing.
export function rankedDisplayName(row) {
  if (!row) return '';
  const name = [row.fName, row.lName].filter(s => s != null && String(s).trim() !== '').join(' ').trim();
  return name || `#${row.patNum}`;
}

const norm = (arr) => new Set((arr || []).map(Number));

/**
 * Apply pin/exclude to the backend-ranked rows for DISPLAY.
 *  - pinned rows float to the top, KEEPING their relative score order among themselves;
 *  - non-pinned rows keep their score order below;
 *  - excluded rows stay in the table (so the user can un-exclude) but are flagged.
 * Returns rows decorated with { _rank, _pinned, _excluded }. `_rank` numbers only the
 * EFFECTIVE (non-excluded) rows, in their displayed order.
 *
 * @param {Array} ranked   backend rows (already in dial/score order)
 * @param {{pin?:number[], exclude?:number[]}} config
 */
export function applyPinExclude(ranked, config) {
  const rows = Array.isArray(ranked) ? ranked.slice() : [];
  const pin = norm(config && config.pin);
  const exclude = norm(config && config.exclude);

  const decorated = rows.map((r, i) => ({
    ...r,
    _origIndex: i,
    _pinned: pin.has(Number(r.patNum)),
    _excluded: exclude.has(Number(r.patNum)),
  }));

  // Stable sort: pinned first, then original score order preserved within each group.
  decorated.sort((a, b) => {
    if (a._pinned !== b._pinned) return a._pinned ? -1 : 1;
    return a._origIndex - b._origIndex;
  });

  let rank = 0;
  for (const row of decorated) {
    if (!row._excluded) {
      rank += 1;
      row._rank = rank;
    } else {
      row._rank = null;
    }
    delete row._origIndex;
  }
  return decorated;
}

// Count of patients who will actually be called = total minus excluded (intersected
// with the known audience so a stale exclude can't push the count negative).
export function effectiveCallCount(ranked, config) {
  const rows = Array.isArray(ranked) ? ranked : [];
  const exclude = norm(config && config.exclude);
  return rows.filter(r => !exclude.has(Number(r.patNum))).length;
}

/**
 * Build the config passed to the PREVIEW endpoint: PIN-ONLY (never exclude).
 *
 * Why pin-only: the backend's ranking REMOVES excluded patients from the returned list.
 * If we sent excludes on a re-fetch (draft reopen, preset change, audience change), those
 * patients would vanish from rankedPatients and the user could no longer SEE or UN-exclude
 * them. Pins still influence backend ordering; excludes stay purely client-side for display
 * (applyPinExclude) and are sent only in the LAUNCH payload, where the backend applies them
 * authoritatively. Returns null when there are no pins (keeps the request clean).
 */
export function buildPreviewConfig(config) {
  const pin = Array.from(new Set((config && config.pin ? config.pin : []).map(Number).filter(pn => !isNaN(pn))));
  return pin.length > 0 ? { pin } : null;
}

/**
 * Normalize form.priorityConfig for sending: a plain { pin:[...], exclude:[...] } with
 * numeric PatNums, intersected with the current audience (drops stale entries). Returns
 * null when there's nothing to send (keeps the payload clean for default/standard).
 */
export function buildPriorityConfig(config, audiencePatNums) {
  const audience = audiencePatNums == null ? null : norm(audiencePatNums);
  const inAudience = (pn) => audience == null || audience.has(pn);
  const clean = (arr) => Array.from(new Set((arr || []).map(Number).filter(pn => !isNaN(pn) && inAudience(pn))));
  const pin = clean(config && config.pin);
  const exclude = clean(config && config.exclude);
  if (pin.length === 0 && exclude.length === 0) return null;
  return { pin, exclude };
}

/**
 * Deduped UNION of the recall audience and the hand-added patients (Phase D.2c).
 *
 * This is the single source of truth for the EFFECTIVE AI-Call audience — it feeds BOTH
 * the preview-ranking fetch AND the launch `selectedList`, so a hand-added patient flows
 * to ranking and to dialing exactly like an audience member.
 *
 * Dedup is by NUMERIC PatNum (so 12 and "12" collapse), audience-first then hand-adds, in
 * first-seen order. Each kept PatNum preserves its original value from whichever list it
 * first appeared in. A hand-added patient who is ALSO in the recall audience is NOT
 * double-counted.
 *
 * @param {Array<number|string>} audiencePatNums   audience PatNums (from activePreviewPatients)
 * @param {Array<number|string>} handAddedPatNums  hand-added PatNums (from form.handAddedPatients)
 * @returns {Array<number|string>} deduped union, audience-first
 */
export function unionPatNums(audiencePatNums, handAddedPatNums) {
  const seen = new Set();
  const out = [];
  for (const pn of [...(audiencePatNums || []), ...(handAddedPatNums || [])]) {
    if (pn == null || pn === '') continue;
    const key = Number(pn);
    if (isNaN(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(pn);
  }
  return out;
}

/**
 * The patients OFFERABLE in the hand-add picker: every patient from the global list
 * (customer.patients) MINUS the ones already in the effective audience — so we never offer
 * a duplicate. Matching is by NUMERIC PatNum (handles number/string mismatch). Rows without
 * a usable PatNum are dropped. Preserves the input order of `allPatients`.
 *
 * @param {Array<{PatNum:(number|string)}>} allPatients     customer.patients rows
 * @param {Array<number|string>} alreadyChosenPatNums       effective audience PatNums
 * @returns {Array} subset of allPatients not already chosen
 */
export function availableToAdd(allPatients, alreadyChosenPatNums) {
  const chosen = norm(alreadyChosenPatNums);
  return (Array.isArray(allPatients) ? allPatients : []).filter(p => {
    if (!p || p.PatNum == null || p.PatNum === '') return false;
    const key = Number(p.PatNum);
    if (isNaN(key)) return false;
    return !chosen.has(key);
  });
}

// Immutable toggle of a PatNum in one of the config arrays (pin|exclude). Pinning a row
// clears any exclude on it and vice-versa, so a patient can't be both at once.
export function togglePriorityConfig(config, key, patNum) {
  const pn = Number(patNum);
  const other = key === 'pin' ? 'exclude' : 'pin';
  const cur = new Set((config && config[key] ? config[key] : []).map(Number));
  const otherSet = new Set((config && config[other] ? config[other] : []).map(Number));
  if (cur.has(pn)) cur.delete(pn);
  else { cur.add(pn); otherSet.delete(pn); }
  return {
    [key]: Array.from(cur),
    [other]: Array.from(otherSet),
  };
}
